/** @format */

import React, {
	useEffect,
	useState,
	useRef,
	useCallback,
	useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { motion, AnimatePresence } from "framer-motion";
import {
	Menu,
	X,
	ShoppingCart,
	SwatchBook,
	UserPlus2,
	User2,
	ChevronDown,
	Search,
	Info,
	Truck,
	LifeBuoy,
} from "lucide-react"; // Refined Icons

// Actions
import { logout } from "../../actions/userActions";
import ToggleCountry from "./ToggleCountry"; // Assuming path
// Removed allProducts import - fetch dynamically or use simpler structure

const NAV_TRANSITION_DURATION = 0.2;

// --- Reusable Dropdown Link Component (for Desktop) ---
const NavLink = ({ item, activePath }) => {
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const hasSubmenu = item.submenu && item.submenu.length > 0;
	const isCurrentPath =
		activePath === item.link ||
		(item.link !== "/" && activePath.startsWith(item.link)); // Basic path matching

	return (
		<div
			className="relative"
			onMouseEnter={() => hasSubmenu && setIsDropdownOpen(true)}
			onMouseLeave={() => hasSubmenu && setIsDropdownOpen(false)}
			// Add focus handlers for keyboard navigation accessibility
			onFocusCapture={() => hasSubmenu && setIsDropdownOpen(true)}
			onBlurCapture={(e) => {
				// Close only if focus moves *outside* the entire dropdown group
				if (hasSubmenu && !e.currentTarget.contains(e.relatedTarget)) {
					setIsDropdownOpen(false);
				}
			}}
		>
			<Link
				to={item.link || "#"} // Use '#' if it's just a dropdown trigger
				className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 focus:ring-offset-secondary ${
					isCurrentPath
						? "text-primary bg-white/10" // Highlight active link
						: "text-white hover:bg-white/10"
				}`}
				// ARIA attributes for dropdowns
				aria-haspopup={hasSubmenu}
				aria-expanded={hasSubmenu ? isDropdownOpen : undefined}
				onClick={(e) => {
					if (item.link === "#") e.preventDefault();
				}} // Prevent navigation for '#' links
			>
				{item.label}
				{hasSubmenu && (
					<ChevronDown
						className={`w-4 h-4 transition-transform duration-200 ${
							isDropdownOpen ? "rotate-180" : ""
						}`}
						aria-hidden="true"
					/>
				)}
			</Link>

			{/* Desktop Dropdown Menu */}
			{hasSubmenu && (
				<AnimatePresence>
					{isDropdownOpen && (
						<motion.div
							initial={{ opacity: 0, y: -5 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -5 }}
							transition={{
								duration: NAV_TRANSITION_DURATION,
								ease: "easeOut",
							}}
							className="absolute top-full left-0 mt-1 w-60 p-2 bg-secondary rounded-lg shadow-xl border border-white/10 z-50" // Overlap slightly if needed via mt-0 or negative margin
						>
							{/* Simple List for single-level dropdown */}
							{item.submenu
								.sort((a, b) => a.label.localeCompare(b.label))
								.map((subItem, subIndex) => (
									<Link
										key={subIndex}
										to={subItem.link || "#"} // Handle links or potential further submenus differently if needed
										className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-primary/80 rounded-md transition-colors duration-150 focus:outline-none focus:bg-primary/80"
										role="menuitem"
										onClick={() => {
											setIsDropdownOpen(false);
											subItem.action && subItem.action();
										}} // Close dropdown on click
									>
										{subItem.label}
									</Link>
								))}
						</motion.div>
					)}
				</AnimatePresence>
			)}
		</div>
	);
};

// --- Main Header Component ---
const Header = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const navigate = useNavigate();
	const isDesktop = useMediaQuery({ query: "(min-width: 1024px)" }); // Use a specific breakpoint (e.g., lg)

	// States
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const [isScrolled, setIsScrolled] = useState(false);
	const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState(null); // Track open mobile submenu index

	// Redux Selectors
	const cartItemCount = useSelector(
		(state) => state.cart?.cartItems?.length || 0
	);
	const swatchCartItemCount = useSelector(
		(state) => state.swatchCart?.swatchCartItems?.length || 0
	);
	const { userInfo, isGuest } = useSelector((state) => state.userLogin);
	const loggedInUser = !isGuest ? userInfo : null; // Clearer variable for logged-in user

	// --- Navigation Structure ---
	const navItems = useMemo(
		() => [
			{
				label: "Shop Shades",
				link: "/shop", // Main link goes to all products page
				submenu: [
					// Direct links to main categories
					{ label: "Roller Shades", link: "/shop/info/roller" },
					{ label: "Zebra Shades", link: "/shop/info/zebra" },
					{ label: "Cellular Shades", link: "/shop/info/simply_cellular" },
					{ label: "Sheer Shades", link: "/shop/info/sheer" },
					// Add other primary categories if applicable
					{ label: "View All", link: "/shop" }, // Link back to main shop page
				],
			},
			{ label: "Motorization", link: "/motorized-solutions" },
			{ label: "Free Samples", link: "/swatches" }, // Promote samples
			{
				label: "Support", // Renamed 'Help'
				link: "#", // Trigger dropdown
				submenu: [
					{ label: "How to Measure", link: "/guides/measuring-guide" },
					{ label: "Installation Guides", link: "/guides/installation-guide" },
					{ label: "Fabric & Buying Guides", link: "/guides/productselection" }, // Combined guides
					{ label: "Warranty", link: "/help/warranty" },
					{ label: "Shipping Policy", link: "/help/shipping-policy" },
					{ label: "Return Policy", link: "/help/return-policy" },
					{ label: "FAQ", link: "/help/faq" }, // Add FAQ if exists
					{ label: "Contact Us", link: "/contact" },
					// Policies could be grouped further if needed
				],
			},
			{ label: "About Us", link: "/about" },
			{ label: "Blog", link: "/blog" }, // Optional based on importance
		],
		[]
	); // Empty dependency array, structure is static

	const profileMenuItems = useMemo(
		() => [
			{ label: "Refer a Friend", link: "/refer-a-friend" },
			{ label: "My Profile", link: "/profile" },
			{ label: "My Orders", link: "/myorders" },
			{
				label: "Logout",
				action: () => {
					dispatch(logout()).then(() => navigate("/")); // Redirect to login after logout
				},
			}, // Use action for logout
		],
		[dispatch]
	);

	// --- Effects ---

	// Scroll detection for potential header style changes (optional)
	useEffect(() => {
		const handleScroll = () => setIsScrolled(window.scrollY > 20); // Smaller threshold
		window.addEventListener("scroll", handleScroll, { passive: true });
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	// Close mobile menu on route change
	useEffect(() => {
		setIsMobileMenuOpen(false);
		setMobileSubmenuOpen(null); // Reset mobile submenu too
	}, [location.pathname]);

	// --- Handlers ---
	const toggleMobileMenu = useCallback(
		() => setIsMobileMenuOpen((prev) => !prev),
		[]
	);
	const toggleMobileSubmenu = useCallback((index) => {
		setMobileSubmenuOpen((prev) => (prev === index ? null : index));
	}, []);
	const handleMobileNavClick = (link) => {
		if (link && link !== "#") {
			setIsMobileMenuOpen(false);
			setMobileSubmenuOpen(null);
			navigate(link); // Use navigate for internal links
		}
	};

	// --- Render ---

	// Don't render header on specific pages if needed
	// if (location.pathname === "/special-offer") return null;

	return (
		<motion.header
			// Use 'fixed' for guaranteed stickiness, 'sticky' can be affected by parents
			className={`fixed w-full top-0 z-50 transition-shadow duration-200 ${
				isScrolled ? "shadow-lg" : "shadow-md"
			}`}
			// Initial animation only on first load? Or remove if jarring?
			// initial={{ y: -100 }} animate={{ y: 0 }} transition={{ duration: 0.5, ease: 'easeOut' }}
		>
			{/* 1. Announcement Bar (Simplified) */}
			<div className="bg-primary text-white text-xs py-1.5 text-center px-4">
				<p>
					<Truck size={14} className="inline-block mr-1.5 -mt-0.5" />
					Free Shipping Canada-Wide
					<span className="mx-2 opacity-50">|</span>
					<SwatchBook size={14} className="inline-block mr-1.5 -mt-0.5" />5 Free
					Fabric Samples
					<span className="mx-2 opacity-50">|</span>
					<LifeBuoy size={14} className="inline-block mr-1.5 -mt-0.5" />
					Expert Support
				</p>
			</div>

			{/* 2. Main Navigation Bar */}
			<nav className="bg-secondary" aria-label="Main Navigation">
				<div className="container mx-auto px-4">
					<div className="flex items-center justify-between h-16">
						{/* Logo */}
						<Link
							to="/"
							className="flex-shrink-0"
							aria-label="Off Cut Shades Home"
						>
							<motion.img
								src="/images/logoDark-1.png" // Verify path
								alt="Off Cut Shades Logo"
								className="h-10 md:h-12" // Slightly adjust size
								whileHover={{ scale: 1.05 }}
							/>
						</Link>

						{/* Desktop Navigation (Hidden on Mobile) */}
						<div className="hidden lg:flex items-center space-x-1">
							{" "}
							{/* Reduced spacing */}
							{navItems.map((item, index) => (
								<NavLink
									key={item.label || index}
									item={item}
									activePath={location.pathname}
								/>
							))}
						</div>

						{/* Right Side Icons + Mobile Toggle */}
						<div className="flex items-center gap-3 md:gap-5">
							{/* Country Toggle */}
							<ToggleCountry />
							{/* Separator (Desktop only) */}
							<div className="hidden lg:block w-px h-6 bg-white/20"></div>

							{/* Icons - Common for Desktop & Mobile Header Bar */}
							{/* Note: Samples link is already in main nav, maybe remove icon? */}
							{/* <Link to="/swatches" className="relative p-2 text-white hover:bg-white/10 rounded-full" title="Free Samples">
                                 <SwatchBook size={20} />
                                 {swatchCartItemCount > 0 && ( <span className="absolute -top-0.5 -right-0.5 bg-primary text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">{swatchCartItemCount}</span> )}
                             </Link> */}
							<Link
								to="/cart"
								className="relative p-2 text-white hover:bg-white/10 rounded-full"
								title="Shopping Cart"
							>
								<ShoppingCart size={20} />
								{cartItemCount > 0 && (
									<span className="absolute -top-0.5 -right-0.5 bg-primary text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
										{cartItemCount}
									</span>
								)}
							</Link>
							{/* Account / Login */}
							<div className="relative hidden lg:block">
								{" "}
								{/* Hide complex dropdown trigger on mobile bar */}
								<NavLink
									item={{
										label: loggedInUser
											? `Hi, ${loggedInUser.name?.split(" ")[0]}`
											: "Account",
										link: loggedInUser ? "#" : "/login", // Link to login if not logged in
										submenu: loggedInUser
											? profileMenuItems
											: [
													{ label: "Refer a Friend", link: "/refer-a-friend" },
													{ label: "Login", link: "/login" },
													{ label: "Sign Up", link: "/register" },
											  ],
									}}
									activePath={location.pathname}
								/>
							</div>
							{/* Simple Account Icon for Mobile Header */}
							<Link
								to={loggedInUser ? "/profile" : "/login"}
								className="relative p-2 text-white hover:bg-white/10 rounded-full lg:hidden"
								title={loggedInUser ? "My Account" : "Login / Sign Up"}
							>
								<User2 size={20} />
							</Link>

							{/* Mobile Menu Button */}
							<button
								onClick={toggleMobileMenu}
								className="p-2 text-white lg:hidden hover:bg-white/10 rounded-md" // Show only on mobile (< lg)
								aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
								aria-expanded={isMobileMenuOpen}
								aria-controls="mobile-menu"
							>
								{isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
							</button>
						</div>
					</div>
				</div>
			</nav>

			{/* 3. Mobile Menu Panel */}
			{!isDesktop &&
				isMobileMenuOpen && ( // Render only on mobile and when open
					<motion.div
						id="mobile-menu"
						initial={{ opacity: 0, height: 0 }}
						animate={{ opacity: 1, height: "auto" }}
						exit={{ opacity: 0, height: 0 }}
						transition={{
							duration: NAV_TRANSITION_DURATION,
							ease: "easeInOut",
						}}
						className="lg:hidden bg-secondary border-t border-white/10 absolute w-full shadow-lg" // Use absolute positioning
						// Optional: Add max-height and overflow-y-auto for long menus
						// style={{ maxHeight: 'calc(100vh - 8rem)', overflowY: 'auto' }}
					>
						<div className="px-4 pt-4 pb-6 space-y-1">
							{navItems.map((item, index) => {
								const hasSubmenu = item.submenu && item.submenu.length > 0;
								const isSubmenuCurrentlyOpen = mobileSubmenuOpen === index;
								return (
									<div key={item.label || index}>
										{/* Main Mobile Nav Item */}
										<button
											onClick={() =>
												hasSubmenu
													? toggleMobileSubmenu(index)
													: handleMobileNavClick(item.link)
											}
											className={`flex items-center justify-between w-full text-left py-3 px-4 text-base font-medium text-white rounded-md transition-colors duration-150 ${
												isSubmenuCurrentlyOpen
													? "bg-white/10"
													: "hover:bg-white/10"
											}`}
											aria-expanded={
												hasSubmenu ? isSubmenuCurrentlyOpen : undefined
											}
										>
											<span>{item.label}</span>
											{hasSubmenu && (
												<ChevronDown
													className={`w-5 h-5 transition-transform duration-200 ${
														isSubmenuCurrentlyOpen ? "rotate-180" : ""
													}`}
												/>
											)}
										</button>
										{/* Mobile Submenu */}
										{hasSubmenu && (
											<AnimatePresence>
												{isSubmenuCurrentlyOpen && (
													<motion.div
														initial={{ height: 0, opacity: 0 }}
														animate={{ height: "auto", opacity: 1 }}
														exit={{ height: 0, opacity: 0 }}
														transition={{
															duration: NAV_TRANSITION_DURATION,
															ease: "easeInOut",
														}}
														className="pl-6 pr-2 pt-1 pb-2 space-y-1 border-l-2 border-primary/50 ml-4 overflow-hidden" // Indent submenu
													>
														{item.submenu.map((subItem, subIndex) => (
															<Link
																key={subIndex}
																to={subItem.link || "#"}
																className="block py-2 px-2 text-sm text-gray-200 hover:text-white hover:bg-white/10 rounded"
																onClick={() =>
																	handleMobileNavClick(subItem.link)
																} // Close menu on nav
															>
																{subItem.label}
															</Link>
														))}
													</motion.div>
												)}
											</AnimatePresence>
										)}
									</div>
								);
							})}
							{/* Logged In Mobile Options */}
							{loggedInUser && (
								<div className="pt-4 mt-4 border-t border-white/10 space-y-1">
									{profileMenuItems.map((item, index) =>
										item.link ? (
											<Link
												key={index}
												to={item.link}
												className="block py-3 px-4 text-base font-medium text-white rounded-md hover:bg-white/10"
												onClick={() => handleMobileNavClick(item.link)}
											>
												{" "}
												{item.label}{" "}
											</Link>
										) : (
											<button
												key={index}
												onClick={() => {
													item.action();
													handleMobileNavClick("#");
												}}
												className="block w-full text-left py-3 px-4 text-base font-medium text-white rounded-md hover:bg-white/10"
											>
												{" "}
												{item.label}{" "}
											</button>
										)
									)}
								</div>
							)}
						</div>
					</motion.div>
				)}
		</motion.header>
	);
};

export default Header;
